import { useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { DashboardConfigPayload } from '@/admin/pages/dashboard/dashboard-types'

/**
 * The current tenant's dashboard config (roles / tiles / favourites / brand),
 * served by GET /api/dashboards/config/. The generic dashboard engine renders
 * whatever this returns - onboarding a new client is a new config row on the
 * backend, never an engine change. Config-time data, so cache generously.
 */
export function useDashboardConfig() {
  return useQuery<DashboardConfigPayload>({
    // 'v3' busts any persisted cache of the pre-doc-columns config; config is
    // config-time data but we refetch on mount so a re-seed shows immediately.
    queryKey: ['dashboard-config', 'v3'],
    queryFn: () => api.get('/dashboards/config/').then((r) => r.data),
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
  })
}

/**
 * Computed, tenant-scoped dashboard metrics (GET /api/dashboards/metrics/). A
 * KPI item binds to one of these by key via its `metric` field; the number is
 * real, not seed data. Most metrics honour the dashboard's selected date window
 * (`createdAfter`) so 7d / 30d / Quarter / YTD recompute the headline numbers.
 */
export function useDashboardMetrics(createdAfter?: string | null) {
  return useQuery<Record<string, number>>({
    queryKey: ['dashboard-metrics', createdAfter ?? 'all'],
    queryFn: () =>
      api
        .get('/dashboards/metrics/', {
          params: createdAfter ? { created_after: createdAfter } : {},
        })
        .then((r) => r.data),
    staleTime: 30 * 1000,
  })
}

/** The card builder's building blocks from GET /api/dashboards/catalog/. */
export interface DashboardCatalog {
  /**
   * Shortlisted entities a card can be built from. `measures` lists the measure
   * keys THIS entity can fulfil - the builder offers only these so no
   * combination lands on an empty data path.
   */
  entities: Array<{ key: string; label: string; measures: string[] }>
  /** Master measure metadata (key -> label + resulting data shape). */
  measures: Array<{ key: string; label: string; data_type: string }>
}

/**
 * The catalogue for the Customise card builder: the shortlisted entities and the
 * measures they can combine with. Any entity x measure is buildable - the
 * combinations are open, not a fixed list. Tenant-agnostic and config-time.
 */
export function useDashboardCatalog() {
  return useQuery<DashboardCatalog>({
    // v4: flush any persisted cache from when the catalogue endpoint was
    // `reporting`-gated and returned [] for flag-off tenants (an empty array has
    // no `.entities`, so the card builder's data picker rendered empty).
    queryKey: ['dashboard-catalog', 'v4'],
    queryFn: () => api.get('/dashboards/catalog/').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    // Static reference data, but always revalidate on mount (stale-while-
    // revalidate) so a bad/empty cached value can never get stuck across loads.
    refetchOnMount: 'always',
  })
}

/** A shape-tagged dataset envelope from GET /api/dashboards/data/?key=... */
export interface DashboardDatasetEnvelope {
  data_type?: string
  /** snapshot_number: a single figure. */
  value?: number
  /** "money" -> render `value` in the org currency. */
  format?: string
  series?: Array<{ label: string; total: number; data: Array<{ period: string; value: number }> }>
  segments?: Array<{ label: string; value: number }>
  items?: Array<Record<string, unknown>>
}

/**
 * One live dataset envelope (trend / breakdown / action-list), served by
 * GET /api/dashboards/data/?key=... A chart, leaderboard, or alerts tile that
 * carries a `data_set.key` binds to real, tenant-scoped data this way instead of
 * inline seed data. Honours the dashboard's date window. Disabled (no request)
 * when there is no key, so inline-data tiles never fetch.
 */
export function useDashboardDataset(key?: string | null, createdAfter?: string | null) {
  return useQuery<DashboardDatasetEnvelope>({
    queryKey: ['dashboard-dataset', key ?? '', createdAfter ?? 'all'],
    queryFn: () =>
      api
        .get('/dashboards/data/', {
          params: {
            key,
            ...(createdAfter ? { created_after: createdAfter } : {}),
          },
        })
        .then((r) => r.data),
    enabled: !!key,
    staleTime: 30 * 1000,
  })
}
